import React from "react";
import { Link } from "react-router-dom";

import Header from "../../components/Header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Footer from "../../components/footer/Footer";
import Scrollbar from "../../components/scrollbar/scrollbar";

import WhyDigicomm from "../../components/Home_Section/WhyDigicomm";

import "./contentPages.css";

type Card = {
  title: string;
  text: string;
  link: string;
};

interface LandingProps {
  eyebrow: string;
  title: string;
  intro: string;
  cards: Card[];
}

const Landing: React.FC<LandingProps> = ({
  eyebrow,
  title,
  intro,
  cards,
}) => (
  <div className="content-page inner-page">
    <div className="body_wrap o-clip">
      <Header />

      <main>
        <PageTitle
          pageTitle={eyebrow}
          pagesub={eyebrow}
        />

        <section className="content-overview">
          <div className="container mxw-1650">
            <h2>{title}</h2>

            <p>{intro}</p>
          </div>
        </section>

        <section className="landing-grid-section">
          <div className="container mxw-1650">
            <div className="landing-grid">
              {cards.map((card, i) => (
                <Link
                  to={card.link}
                  className="landing-card"
                  key={card.title}
                >
                  <b>
                    {String(i + 1).padStart(2, "0")}
                  </b>

                  <h2>{card.title}</h2>

                  <p>{card.text}</p>

                  <span>
                    Explore <i>→</i>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="content-cta">
          <div className="container mxw-1650">
            <div>
              <span>Engineering partnership</span>

              <h2>
                Build the right team for your next milestone.
              </h2>
            </div>

            <Link
              to="/contact"
              className="thm-btn"
            >
              Let&apos;s Talk
            </Link>
          </div>
        </section>
      </main>

      <Footer />

      <Scrollbar />
    </div>
  </div>
);


/* =========================================================
   SOLUTIONS PAGE
========================================================= */

export const SolutionsPage = () => (
  <Landing
    eyebrow="Solutions"
    title="Flexible delivery models for complex engineering programs."
    intro="Engage Digicomm for targeted capacity, complete work-package ownership or integrated product and silicon development."
    cards={[
      {
        title: "Engineering Team Extension",
        text:
          "Specialist engineers integrated directly into your team and delivery rhythm.",
        link: "/solutions/engineering-team-extension",
      },
      {
        title: "Project Ownership",
        text:
          "Defined scope, accountable execution and measurable delivery outcomes.",
        link: "/solutions/project-ownership",
      },
      {
        title: "End-to-End Silicon Development",
        text:
          "One coordinated engineering partner from architecture through tapeout and validation.",
        link: "/solutions/end-to-end-silicon-development",
      },
      {
        title: "Engineering Product Design",
        text:
          "System-level engineering that turns product requirements into validated designs.",
        link: "/solutions/engineering-product-design",
      },
      {
        title: "Product Realization",
        text:
          "A practical route from engineering concept to deployable, manufacturable product.",
        link: "/solutions/product-realization",
      },
    ]}
  />
);


/* =========================================================
   TAPEOUT EXPERIENCE PAGE
========================================================= */

export const TapeoutsPage = () => (
  <Landing
    eyebrow="Tapeout Experience"
    title="Disciplined execution from design closure to silicon release."
    intro="Our engineering teams support tapeout readiness, cross-domain signoff, foundry coordination and milestone governance."
    cards={[
      {
        title: "Tapeout Readiness",
        text:
          "Structured reviews across design, verification, DFT, timing and physical signoff.",
        link: "/services/silicon-realization",
      },
      {
        title: "Signoff Coordination",
        text:
          "Clear ownership of dependencies, waivers, quality checks and final release criteria.",
        link: "/services/physical-design",
      },
      {
        title: "Program Governance",
        text:
          "Transparent milestone tracking, risk visibility and executive reporting.",
        link: "/solutions/project-ownership",
      },
      {
        title: "Post-Silicon Continuity",
        text:
          "Validation and debug support that carries knowledge beyond tapeout.",
        link: "/services/silicon-validation",
      },
    ]}
  />
);


/* =========================================================
   INDUSTRIES PAGE
========================================================= */

export const IndustriesPage = () => (
  <Landing
    eyebrow="Industries"
    title="Semiconductor engineering for products that must perform reliably."
    intro="Digicomm applies cross-domain engineering to high-growth and mission-critical technology markets."
    cards={[
      {
        title: "Automotive",
        text:
          "Engineering support for safety-conscious, connected and compute-intensive vehicle platforms.",
        link: "/contact",
      },
      {
        title: "Data Center & Cloud",
        text:
          "High-performance compute, networking, acceleration and platform enablement.",
        link: "/contact",
      },
      {
        title: "Communications",
        text:
          "Silicon and software expertise for wired, wireless and network infrastructure.",
        link: "/contact",
      },
      {
        title: "Industrial & IoT",
        text:
          "Reliable embedded, sensing, control and connectivity solutions.",
        link: "/contact",
      },
      {
        title: "Consumer Electronics",
        text:
          "Power-efficient silicon and product engineering for connected devices.",
        link: "/contact",
      },
      {
        title: "Aerospace & Defense",
        text:
          "Methodical engineering for long-lifecycle and high-reliability systems.",
        link: "/contact",
      },
    ]}
  />
);


/* =========================================================
   CASE STUDIES PAGE
========================================================= */

export const CaseStudiesPage = () => (
  <Landing
    eyebrow="Case Studies"
    title="Engineering challenges converted into dependable outcomes."
    intro="Representative engagements showing how Digicomm combines specialist depth, ownership and flexible delivery."
    cards={[
      {
        title: "SoC Verification Acceleration",
        text:
          "A reusable verification environment and closure plan for a complex multi-IP subsystem.",
        link: "/solutions/project-ownership",
      },
      {
        title: "Physical Design Closure",
        text:
          "Focused implementation support to resolve congestion, timing and signoff risks.",
        link: "/services/physical-design",
      },
      {
        title: "Platform Bring-Up",
        text:
          "Integrated hardware, firmware and validation support for a new silicon platform.",
        link: "/solutions/engineering-product-design",
      },
      {
        title: "Post-Silicon Automation",
        text:
          "Lab automation and analytics that shortened validation and debug cycles.",
        link: "/services/silicon-validation",
      },
    ]}
  />
);


/* =========================================================
   WHY DIGICOMM PAGE
   Reuses homepage WhyDigicomm component
========================================================= */

export const WhyDigicommPage = () => (
  <div className="content-page inner-page">
    <div className="body_wrap o-clip">
      <Header />

      <main>
        <PageTitle
          pageTitle="Why Digicomm"
          pagesub="Why Digicomm"
        />

        <WhyDigicomm />
      </main>

      <Footer />

      <Scrollbar />
    </div>
  </div>
);